import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import { useSelector } from 'react-redux';
import Layout from '../../components/global/layout';
import environment from '../../environment';
import loader from '../../methods/loader';
import { FaUsers, FaCalendarCheck, FaExclamationTriangle, FaUserPlus, FaChartBar } from 'react-icons/fa';

// cw: small affordances so users know cards/rows are clickable/expandable
const CornerChevron = ({ open }: { open: boolean }) => (
    <span
        className={`absolute top-4 right-4 text-sm transition-transform ${open ? 'rotate-90 text-orange-500' : 'text-gray-300'}`}
    >
        &#9656;
    </span>
);

const ClickHint = ({ open, label }: { open: boolean, label?: string }) => (
    <p className="text-[11px] font-medium text-orange-500 mt-2 flex items-center gap-1">
        <span className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>&#9656;</span>
        {open ? 'Click to hide' : (label || 'Click to view members')}
    </p>
);

const MemberDropdown = ({ members, label }: { members: any[], label?: string }) => {
    if (!members?.length) return <p className="text-xs text-gray-400 mt-2 italic">No members</p>;
    return (
        <div className="mt-3 border-t border-gray-100 pt-3 max-h-48 overflow-y-auto">
            {label && <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>}
            <div className="space-y-1">
                {members.map((m: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1">
                        <span className="text-gray-700">{m.name}</span>
                        {m.count !== undefined && <span className="text-xs text-gray-400">{m.count} events ({m.pct}%)</span>}
                        {m.email && !m.count && m.count !== 0 && <span className="text-xs text-gray-400">{m.email}</span>}
                        {m.first6Attendance !== undefined && <span className="text-xs text-gray-400">{m.first6Attendance} of first 6</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AttendanceData = () => {
    const user = useSelector((state: any) => state.user);
    const history = useNavigate();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState(6);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [trendEvents, setTrendEvents] = useState<any[]>([]);
    const [trendHasMore, setTrendHasMore] = useState(false);
    const [trendLoading, setTrendLoading] = useState(false);
    const trendSkipRef = useRef(0);
    const trendScrollRef = useRef<HTMLDivElement>(null);
    const groupIdRef = useRef<string>('');

    const toggle = (key: string) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    useEffect(() => {
        const roleId = user?.customerRole?._id || user?.customerRole;
        if (roleId && !environment.groupLeaderRoleIds?.includes(roleId)) {
            history('/dashboard');
        }
    }, [user]);

    const loadTrend = useCallback((gId: string, skip: number) => {
        if (trendLoading) return;
        setTrendLoading(true);
        ApiClient.post('api/engagement/attendance-trend', { groupId: gId, skip, limit: 12 }).then((res: any) => {
            setTrendLoading(false);
            if (res.success) {
                setTrendEvents(prev => skip === 0 ? res.data : [...prev, ...res.data]);
                setTrendHasMore(res.hasMore);
                trendSkipRef.current = skip + res.data.length;
            }
        }).catch(() => setTrendLoading(false));
    }, []);

    const loadData = useCallback((groupId: string, m: number) => {
        loader(true);
        ApiClient.post('api/engagement/attendance-data', { groupId, months: m }).then((res: any) => {
            loader(false);
            setLoading(false);
            if (res.success) {
                setData(res.data);
                setTrendEvents(res.data.attendanceTrend || []);
                setTrendHasMore((res.data.attendanceTrend?.length || 0) < (res.data.totalEventsAllTime || 0));
                trendSkipRef.current = res.data.attendanceTrend?.length || 0;
            }
        }).catch(() => {
            loader(false);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        // cw: scope attendance to the user's CURRENT group (lastUsedGroupId), not just the first
        // active group in the array. A GL in groups A/B/C whose current group is A must see only A's
        // attendance data — matches the lastUsedGroupId convention used across Members/History/Events.
        const activeGroup = user?.groups?.find((g: any) => g.status === 'active');
        const groupId = user?.lastUsedGroupId?._id || user?.lastUsedGroupId
            || user?.groupId?._id || user?.groupId
            || activeGroup?.groupId?._id || activeGroup?.groupId;
        if (!groupId) return;
        groupIdRef.current = groupId;
        loadData(groupId, months);
    }, [user]);

    // cw: re-fetch attendance-frequency window when the months dropdown changes
    const onMonthsChange = (m: number) => {
        setMonths(m);
        if (groupIdRef.current) loadData(groupIdRef.current, m);
    };

    const handleTrendScroll = useCallback(() => {
        const el = trendScrollRef.current;
        if (!el || trendLoading || !trendHasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
            loadTrend(groupIdRef.current, trendSkipRef.current);
        }
    }, [trendLoading, trendHasMore, loadTrend]);

    if (loading) return <Layout><div className="p-6 text-gray-500">Loading attendance data...</div></Layout>;
    if (!data || data.noData) return <Layout><div className="p-6 text-gray-500">No event data available yet for this group.</div></Layout>;

    return (
        <Layout>
            <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">Attendance Data</h2>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div
                        className="relative bg-white rounded-xl shadow p-5 cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all"
                        onClick={() => toggle('active')}
                    >
                        <CornerChevron open={expanded.has('active')} />
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <FaUsers className="text-green-600" />
                            </div>
                            <span className="text-sm text-gray-500">Active Member Rate</span>
                        </div>
                        <p className="text-3xl font-bold text-green-600">{data.activeMemberRate}%</p>
                        <p className="text-xs text-gray-400 mt-1">{data.activeMembers} of {data.totalMembers} attended in last 3 months</p>
                        <ClickHint open={expanded.has('active')} />
                        {expanded.has('active') && (
                            <MemberDropdown members={data.activeMembersList} label={`${data.activeMembers} active members`} />
                        )}
                    </div>

                    <div
                        className="relative bg-white rounded-xl shadow p-5 cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all"
                        onClick={() => toggle('unique')}
                    >
                        <CornerChevron open={expanded.has('unique')} />
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <FaCalendarCheck className="text-blue-600" />
                            </div>
                            <span className="text-sm text-gray-500">Unique Attendees (12mo)</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-600">{data.uniqueAttendees12mo}</p>
                        <p className="text-xs text-gray-400 mt-1">Rolling 12-month window</p>
                        <ClickHint open={expanded.has('unique')} />
                        {expanded.has('unique') && (
                            <MemberDropdown members={data.uniqueAttendeesList} label={`${data.uniqueAttendees12mo} unique attendees`} />
                        )}
                    </div>

                    <div
                        className="relative bg-white rounded-xl shadow p-5 cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all"
                        onClick={() => toggle('atrisk')}
                    >
                        <CornerChevron open={expanded.has('atrisk')} />
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                <FaExclamationTriangle className="text-orange-600" />
                            </div>
                            <span className="text-sm text-gray-500">At-Risk Members</span>
                        </div>
                        <p className="text-3xl font-bold text-orange-600">{data.atRiskMembers?.length || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">Active early, absent last 3 events</p>
                        <ClickHint open={expanded.has('atrisk')} />
                        {expanded.has('atrisk') && (
                            <MemberDropdown members={data.atRiskMembers} label="At-risk members" />
                        )}
                    </div>

                    <div
                        className="relative bg-white rounded-xl shadow p-5 cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all"
                        onClick={() => toggle('conversion')}
                    >
                        <CornerChevron open={expanded.has('conversion')} />
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <FaUserPlus className="text-purple-600" />
                            </div>
                            <span className="text-sm text-gray-500">Visitor Conversion</span>
                        </div>
                        <p className="text-3xl font-bold text-purple-600">{data.visitorConversionRate}%</p>
                        <p className="text-xs text-gray-400 mt-1">{data.convertedGuests} of {data.totalGuests} guests became members</p>
                        <ClickHint open={expanded.has('conversion')} label="Click to view guests" />
                        {expanded.has('conversion') && (
                            <div className="mt-3 border-t border-gray-100 pt-3 max-h-48 overflow-y-auto">
                                {data.convertedGuestsList?.length > 0 && (
                                    <>
                                        <p className="text-xs font-medium text-green-600 mb-1">Converted ({data.convertedGuestsList.length})</p>
                                        {data.convertedGuestsList.map((m: any, i: number) => (
                                            <div key={i} className="text-sm text-gray-700 py-0.5">{m.name}</div>
                                        ))}
                                    </>
                                )}
                                {data.unconvertedGuestsList?.length > 0 && (
                                    <>
                                        <p className="text-xs font-medium text-gray-400 mb-1 mt-2">Not converted ({data.unconvertedGuestsList.length})</p>
                                        {data.unconvertedGuestsList.slice(0, 20).map((m: any, i: number) => (
                                            <div key={i} className="text-sm text-gray-500 py-0.5">{m.name}</div>
                                        ))}
                                        {data.unconvertedGuestsList.length > 20 && (
                                            <p className="text-xs text-gray-400 mt-1">+ {data.unconvertedGuestsList.length - 20} more</p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Attendance Frequency Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow p-5">
                        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                                <FaChartBar className="text-gray-400" />
                                Attendance Frequency (Last {months} Months)
                            </h3>
                            <select
                                value={months}
                                onChange={(e) => onMonthsChange(Number(e.target.value))}
                                className="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-200"
                            >
                                <option value={3}>Last 3 Months</option>
                                <option value={6}>Last 6 Months</option>
                                <option value={12}>Last 12 Months</option>
                            </select>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">{data.totalEventsWindow ?? data.totalEvents6mo} events in this period</p>
                        <div className="space-y-3">
                            {[
                                { label: '90-100%', key: '90-100', color: 'bg-green-500' },
                                { label: '50-89%', key: '50-89', color: 'bg-blue-500' },
                                { label: '25-49%', key: '25-49', color: 'bg-yellow-500' },
                                { label: '1-24%', key: '1-24', color: 'bg-orange-500' },
                                { label: '0%', key: '0', color: 'bg-red-500' },
                            ].map(({ label, key, color }) => {
                                const count = data.distribution?.[key] || 0;
                                const total = Object.values(data.distribution || {}).reduce((s: number, v: any) => s + v, 0) as number;
                                const pct = total > 0 ? (count / total) * 100 : 0;
                                const distKey = `dist-${key}`;
                                const members = data.distributionMembers?.[key] || [];
                                const isOpen = expanded.has(distKey);
                                return (
                                    <div
                                        key={key}
                                        className="cursor-pointer hover:bg-gray-100 rounded-lg p-2 -mx-2 transition-colors"
                                        onClick={() => toggle(distKey)}
                                        title="Click to view members"
                                    >
                                        <div className="flex justify-between items-center text-sm mb-1">
                                            <span className="text-gray-600">{label} attendance</span>
                                            <span className="flex items-center gap-1 text-orange-500 font-medium underline decoration-dotted underline-offset-2">
                                                <span className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                                                {count} member{count !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                                            <div className={`${color} h-2.5 rounded-full transition-all`} style={{ width: `${Math.max(pct, 2)}%` }} />
                                        </div>
                                        {!isOpen && count > 0 && (
                                            <p className="text-[11px] text-gray-400 mt-1">click to view</p>
                                        )}
                                        {isOpen && (
                                            <MemberDropdown members={members} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Attendance Trend */}
                    <div className="bg-white rounded-xl shadow p-5">
                        <h3 className="font-semibold text-lg mb-4">Attendance Per Event</h3>
                        <div ref={trendScrollRef} onScroll={handleTrendScroll} className="space-y-2 max-h-96 overflow-y-auto">
                            {trendEvents.map((ev: any, i: number) => {
                                const trendKey = `trend-${i}`;
                                const isOpen = expanded.has(trendKey);
                                const noShows = ev.noShows || [];
                                const noShowCount = ev.noShowCount ?? noShows.length;
                                return (
                                    <div key={i} className="py-2 border-b border-gray-50 last:border-0">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{ev.title}</p>
                                                <p className="text-xs text-gray-400">{new Date(ev.date).toLocaleDateString()}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm text-gray-600">{ev.present}/{ev.total} attended</span>
                                                <div className="w-16 bg-gray-100 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full ${ev.rate >= 75 ? 'bg-green-500' : ev.rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                        style={{ width: `${ev.rate}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs font-medium w-8 text-right">{ev.rate}%</span>
                                            </div>
                                        </div>
                                        {noShowCount > 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => toggle(trendKey)}
                                                className="mt-1 flex items-center gap-1 text-[11px] font-medium text-orange-500 hover:underline"
                                            >
                                                <span className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                                                {noShowCount} RSVP'd yes, didn't attend
                                            </button>
                                        ) : (
                                            <p className="mt-1 text-[11px] text-gray-300">All RSVPs attended</p>
                                        )}
                                        {isOpen && noShowCount > 0 && (
                                            <div className="mt-2 ml-4 border-l-2 border-orange-100 pl-3 space-y-1">
                                                {noShows.map((n: any, j: number) => (
                                                    <div key={j} className="flex items-center justify-between text-xs">
                                                        <span className="text-gray-600">{n.name || n.email}</span>
                                                        {n.email && n.name && <span className="text-gray-400">{n.email}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {trendLoading && <p className="text-xs text-gray-400 text-center py-2">Loading more...</p>}
                            {!trendHasMore && trendEvents.length > 0 && <p className="text-xs text-gray-300 text-center py-2">All events loaded</p>}
                        </div>
                    </div>
                </div>

                {/* At-Risk Members List */}
                {data.atRiskMembers?.length > 0 && (
                    <div className="bg-white rounded-xl shadow p-5">
                        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                            <FaExclamationTriangle className="text-orange-400" />
                            Lapsing / At-Risk Members
                        </h3>
                        <p className="text-xs text-gray-400 mb-3">Attended 3+ of their first 6 meetings but zero of the last 3</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {data.atRiskMembers.map((m: any, i: number) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                                    <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center text-xs font-bold text-orange-700">
                                        {(m.name || '?')[0]}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">{m.name}</p>
                                        <p className="text-xs text-gray-400">Attended {m.first6Attendance} of first 6</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AttendanceData;
